import { set } from '@ember/object';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib class — paginated list search: back to page 1 when the search
 *              term or any filter changes while the page number did not
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      @ember/object set
 *   siblings:  services/index-context.js (searchItem → resetPageOnFilterChange);
 *              beebee-base-template controllers/signed-document.js,
 *              controllers/communications/index.js (searchItem)
 *   tests:     index-page-reset-test.js
 *   gotchas:   (1) The signature is the outgoing query minus `pag` and the
 *              volatile keys (`_t`, `checks`): every filter the server sees counts,
 *              no per-list allowlist to drift. (2) Only a change made while the page
 *              stayed the same resets: a page click (same filters) and a history
 *              back/forward that restores q and pag together are left alone.
 *              (3) The first search after `forget()`, or under another scope, only
 *              records — a reload or a link to `?q=x&pag=3` keeps page 3. Scope is
 *              compared by identity: index-context passes the list route's model,
 *              a new object on every route entry (no refreshModel QPs there), so a
 *              re-entry or another list is a fresh start with no bookkeeping.
 *
 * Before: search a term with more than one page of results, go to page 2, then
 * change `q` → the new query was sent with `pag=2`; when the narrower result set
 * had a single page the list came back empty.
 */
const VOLATILE_KEYS = new Set(['pag', '_t', 'checks']);
const normalize = value => {
  if (value === undefined || value === null || value === '') return null;
  if (Array.isArray(value)) return value.length ? value.map(String) : null;
  return String(value);
};

/** Stable string of every query key except the page and the volatile ones. */
const filterSignature = query => JSON.stringify(Object.keys(query || {}).filter(key => !VOLATILE_KEYS.has(key)).sort().map(key => [key, normalize(query[key])]));
const pageOf = value => {
  const page = Number.parseInt(value, 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
};
class IndexPageReset {
  constructor() {
    this.scope = undefined;
    this.signature = null;
    this.page = null;
  }
  /** The next search only records (route entry: a deep link keeps its page). */
  forget() {
    this.scope = undefined;
    this.signature = null;
    this.page = null;
  }

  /**
   * Call right before a search is sent. When the filters changed and the page did
   * not, writes `pag = 1` on `target` and on `query`.
   * @param {object} target - controller holding the `pag` query param
   * @param {object} query - outgoing query (its `pag` is updated in place)
   * @param {*} [scope] - list identity, compared with ===: another one is a fresh
   *   start, not a filter change
   * @returns {boolean} true when the page was reset
   */
  apply(target, query, scope) {
    const signature = filterSignature(query);
    const page = pageOf(target?.pag);
    const sameList = this.signature !== null && this.scope === scope;
    const reset = sameList && this.signature !== signature && page === this.page && page > 1;
    if (reset) {
      set(target, 'pag', 1);
      if (query && 'pag' in query) query.pag = 1;
    }
    this.scope = scope;
    this.signature = signature;
    this.page = reset ? 1 : page;
    return reset;
  }
}
const trackers = new WeakMap();

/**
 * index-context hook: `const query = resetPageOnFilterChange(this, this._buildQuery())`.
 * One tracker per service, scoped to the list route's model object.
 * @param {object} service - service:index-context (reads `controller`)
 * @param {object} query - outgoing query, returned (with `pag` reset when needed)
 * @returns {object} query
 */
function resetPageOnFilterChange(service, query) {
  const controller = service?.controller;
  if (!controller) return query;
  let tracker = trackers.get(service);
  if (!tracker) trackers.set(service, tracker = new IndexPageReset());
  tracker.apply(controller, query, controller.model);
  return query;
}

export { IndexPageReset as default, filterSignature, resetPageOnFilterChange };
//# sourceMappingURL=index-page-reset.js.map
